// Hybrid Messaging Encryption System
// Regular messages: encrypted but admin-readable
// Private mode: true end-to-end encryption

export interface EncryptedMessage {
  id: string
  encryptedContent: string
  iv: string
  isPrivateMode: boolean
  timestamp: number
  senderId: string
  receiverId: string
  salt?: string // Only for private mode
}

export interface KeyPair {
  publicKey: CryptoKey
  privateKey: CryptoKey
}

class EncryptionService {
  private masterKey: CryptoKey | null = null
  private userKeyPairs: Map<string, KeyPair> = new Map()

  // Initialize master key for regular messages (admin can decrypt)
  async initializeMasterKey(): Promise<void> {
    const storedKey = localStorage.getItem('master_key')
    if (storedKey) {
      const keyData = JSON.parse(storedKey)
      this.masterKey = await crypto.subtle.importKey(
        'raw',
        new Uint8Array(keyData),
        { name: 'AES-GCM' },
        true,
        ['encrypt', 'decrypt']
      )
    } else {
      this.masterKey = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      )
      
      const exported = await crypto.subtle.exportKey('raw', this.masterKey)
      localStorage.setItem('master_key', JSON.stringify(Array.from(new Uint8Array(exported))))
    }
  }

  // Generate user key pair for private mode
  async generateUserKeyPair(userId: string): Promise<KeyPair> {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256'
      },
      true,
      ['encrypt', 'decrypt']
    )

    this.userKeyPairs.set(userId, keyPair)
    
    // Store private key securely (in real app, this would be password-protected)
    const privateKey = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey)
    const publicKey = await crypto.subtle.exportKey('spki', keyPair.publicKey)
    
    localStorage.setItem(`private_key_${userId}`, JSON.stringify(Array.from(new Uint8Array(privateKey))))
    localStorage.setItem(`public_key_${userId}`, JSON.stringify(Array.from(new Uint8Array(publicKey))))

    return keyPair
  }

  // Load user keys from storage
  async loadUserKeys(userId: string): Promise<KeyPair | null> {
    const privateKeyData = localStorage.getItem(`private_key_${userId}`)
    const publicKeyData = localStorage.getItem(`public_key_${userId}`)

    if (!privateKeyData || !publicKeyData) return null

    const privateKey = await crypto.subtle.importKey(
      'pkcs8',
      new Uint8Array(JSON.parse(privateKeyData)),
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      true,
      ['decrypt']
    )

    const publicKey = await crypto.subtle.importKey(
      'spki',
      new Uint8Array(JSON.parse(publicKeyData)),
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      true,
      ['encrypt']
    )

    const keyPair = { publicKey, privateKey }
    this.userKeyPairs.set(userId, keyPair)
    return keyPair
  }

  // Encrypt regular message (admin can decrypt)
  async encryptRegularMessage(message: string): Promise<{ encryptedContent: string; iv: string }> {
    if (!this.masterKey) await this.initializeMasterKey()

    const encoder = new TextEncoder()
    const data = encoder.encode(message)
    const iv = crypto.getRandomValues(new Uint8Array(12))

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      this.masterKey!,
      data
    )

    return {
      encryptedContent: Array.from(new Uint8Array(encrypted)).map(b => b.toString(16).padStart(2, '0')).join(''),
      iv: Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join('')
    }
  }

  // Decrypt regular message (admin capability)
  async decryptRegularMessage(encryptedContent: string, iv: string): Promise<string> {
    if (!this.masterKey) await this.initializeMasterKey()

    const encryptedData = new Uint8Array(encryptedContent.match(/.{2}/g)!.map(byte => parseInt(byte, 16)))
    const ivArray = new Uint8Array(iv.match(/.{2}/g)!.map(byte => parseInt(byte, 16)))

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivArray },
      this.masterKey!,
      encryptedData
    )

    const decoder = new TextDecoder()
    return decoder.decode(decrypted)
  }

  // Encrypt private mode message (end-to-end)
  async encryptPrivateMessage(message: string, receiverUserId: string): Promise<{ encryptedContent: string; iv: string; salt: string }> {
    // Get receiver's public key
    const receiverPublicKeyData = localStorage.getItem(`public_key_${receiverUserId}`)
    if (!receiverPublicKeyData) {
      throw new Error('Receiver public key not found')
    }

    const receiverPublicKey = await crypto.subtle.importKey(
      'spki',
      new Uint8Array(JSON.parse(receiverPublicKeyData)),
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    )

    // Generate session key for this message
    const sessionKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    )

    // Encrypt message with session key
    const encoder = new TextEncoder()
    const data = encoder.encode(message)
    const iv = crypto.getRandomValues(new Uint8Array(12))

    const encryptedMessage = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      sessionKey,
      data
    )

    // Encrypt session key with receiver's public key
    const exportedSessionKey = await crypto.subtle.exportKey('raw', sessionKey)
    const encryptedSessionKey = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      receiverPublicKey,
      exportedSessionKey
    )

    return {
      encryptedContent: Array.from(new Uint8Array(encryptedMessage)).map(b => b.toString(16).padStart(2, '0')).join(''),
      iv: Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join(''),
      salt: Array.from(new Uint8Array(encryptedSessionKey)).map(b => b.toString(16).padStart(2, '0')).join('')
    }
  }

  // Decrypt private mode message (end-to-end)
  async decryptPrivateMessage(encryptedContent: string, iv: string, salt: string, userId: string): Promise<string> {
    const keyPair = this.userKeyPairs.get(userId) || await this.loadUserKeys(userId)
    if (!keyPair) {
      throw new Error('User private key not found')
    }

    // Decrypt session key
    const encryptedSessionKey = new Uint8Array(salt.match(/.{2}/g)!.map(byte => parseInt(byte, 16)))
    const sessionKeyData = await crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      keyPair.privateKey,
      encryptedSessionKey
    )

    // Import session key
    const sessionKey = await crypto.subtle.importKey(
      'raw',
      sessionKeyData,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    )

    // Decrypt message
    const encryptedData = new Uint8Array(encryptedContent.match(/.{2}/g)!.map(byte => parseInt(byte, 16)))
    const ivArray = new Uint8Array(iv.match(/.{2}/g)!.map(byte => parseInt(byte, 16)))

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivArray },
      sessionKey,
      encryptedData
    )

    const decoder = new TextDecoder()
    return decoder.decode(decrypted)
  }

  // Admin function to decrypt regular messages (for legal compliance)
  async adminDecryptMessage(message: EncryptedMessage): Promise<string | null> {
    if (message.isPrivateMode) {
      return null // Cannot decrypt private mode messages
    }
    return await this.decryptRegularMessage(message.encryptedContent, message.iv)
  }
}

export const encryptionService = new EncryptionService()